import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { 
  PrinterIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { crmAPI } from '../services/api';
import BrandLogo from '../components/common/BrandLogo';
import toast from 'react-hot-toast';

const ContractPage: React.FC = () => {
    const { contractId } = useParams<{ contractId: string }>();
    const [searchParams] = useSearchParams();
    const [contract, setContract] = useState<any>(null);
    const [project, setProject] = useState<any>(null);
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [password, setPassword] = useState('');
    const [passInput, setPassInput] = useState('');
    const [authenticated, setAuthenticated] = useState(false);
    const [isSigning, setIsSigning] = useState(false);
    const [signatureName, setSignatureName] = useState('');
    const [isPhotographerSigning, setIsPhotographerSigning] = useState(false);
    const [photographerSignatureName, setPhotographerSignatureName] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const { user: currentUser } = useSelector((state: RootState) => state.auth);
    // isOwner checks if the currently logged-in user matches the owner listed on the contract project
    const isOwner = currentUser && user && currentUser.id === user.id;

    const fetchContract = async (pwd: string = password) => {
        try {
            setLoading(true);
            const res = await crmAPI.getPublicContract(contractId!, pwd);
            setContract(res.data.data.contract);
            setProject(res.data.data.project);
            setUser(res.data.data.user);
            setAuthenticated(true);
            setPassword(pwd);
        } catch (err: any) {
            if (err.response?.status === 401) {
                setAuthenticated(false);
            } else {
                toast.error('Failed to load contract');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (contractId) {
            const pwd = searchParams.get('password') || '';
            fetchContract(pwd);
        }
    }, [contractId, searchParams]);

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        fetchContract(passInput);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleSignContract = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!signatureName.trim()) {
            toast.error('Please enter your full name to sign');
            return;
        }

        try {
            setIsSigning(true);
            const res = await crmAPI.signContract(contract.id, signatureName);
            setContract(res.data.data.contract);
            toast.success('Contract signed successfully');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to sign contract');
        } finally {
            setIsSigning(false);
        }
    };

    const handleRejectContract = async () => {
        if (!rejectReason.trim()) {
            toast.error("Please provide a reason for declining.");
            return;
        }
        try {
            setLoading(true);
            const res = await crmAPI.rejectContract(contract.id, rejectReason.trim());
            setContract(res.data.data.contract);
            setShowRejectModal(false);
            toast.success('Contract declined.');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to decline contract');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-white md:bg-gray-50 flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white md:rounded-[3rem] md:shadow-2xl md:shadow-indigo-900/5 p-12 text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8">
                        <LockClosedIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Protected Contract</h1>
                    <p className="text-sm font-medium text-gray-500 mb-8">Please enter the password provided by your photographer to view this document.</p>
                    
                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="password"
                            value={passInput}
                            onChange={(e) => setPassInput(e.target.value)}
                            placeholder="Enter password"
                            className="w-full bg-gray-50 border-none rounded-2xl p-5 text-center text-lg font-bold tracking-widest focus:ring-2 focus:ring-teal-500 transition-all placeholder:text-gray-300 placeholder:font-medium placeholder:text-sm"
                            required
                        />
                        <button 
                            type="submit"
                            className="w-full py-5 bg-teal-500 hover:bg-teal-600 text-white rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-lg shadow-teal-500/20"
                        >
                            Unlock Document
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="text-center">
                    <ExclamationCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h1 className="text-2xl font-black text-gray-900 uppercase">Contract Not Found</h1>
                    <p className="text-gray-500 mt-2">The document you are looking for does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    return (
        <>
        <style>{`
            @media print {
                @page { margin: 16mm 14mm; size: A4; }
                body { background: white !important; }
                .contract-card { box-shadow: none !important; border-radius: 0 !important; }
                .shadow-2xl, .shadow-xl, .shadow-lg, .shadow-md, .shadow-sm, .shadow, .bg-white, .border {
                    box-shadow: none !important;
                }
                .print-hidden { display: none !important; }
                .contract-footer { page-break-before: avoid !important; break-before: avoid !important; }
                .contract-signatures { page-break-inside: avoid !important; break-inside: avoid !important; }
                .contract-body { overflow-wrap: break-word !important; word-break: break-word !important; }
            }
        `}</style>
        <div className="min-h-screen bg-slate-50 font-sans text-gray-900 print:bg-white print:min-h-0 print:h-auto overflow-visible print:overflow-visible">
            {/* Nav / Controls (Hidden on Print) */}
            <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-6 py-4 print:hidden">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                    <div className="flex items-center space-x-3 w-full sm:w-auto">
                        <BrandLogo iconSize="w-9 h-9" textSize="text-xl" />
                        <div className="h-5 w-px bg-gray-200"></div>
                        <h1 className="text-sm font-semibold uppercase tracking-widest text-gray-400 truncate">Contract Document</h1>
                    </div>
                    <div className="flex items-center space-x-3 w-full sm:w-auto">
                        <button 
                            onClick={handlePrint}
                            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-black/10"
                        >
                            <PrinterIcon className="w-4 h-4" />
                            <span>Print / Save PDF</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Status Banner */}
            {(contract.status === 'signed' || contract.client_signature || contract.photographer_signature) && (
                <div className={`border-b px-6 py-4 print:hidden ${contract.status === 'signed' ? 'bg-teal-50 border-teal-100 text-teal-700' : 'bg-indigo-50 border-indigo-100 text-indigo-700'}`}>
                    <div className="max-w-4xl mx-auto flex items-center justify-center space-x-2">
                        {contract.status === 'signed' ? (
                            <>
                                <CheckBadgeIcon className="w-5 h-5" />
                                <span className="text-sm font-bold">Document Fully Executed & Binding</span>
                            </>
                        ) : (
                            <>
                                <div className="animate-pulse w-2 h-2 rounded-full bg-indigo-400" />
                                <span className="text-sm font-bold">
                                    {contract.client_signature && !contract.photographer_signature && `Partially Signed (Awaiting Photographer Countersign)`}
                                    {contract.photographer_signature && !contract.client_signature && `Partially Signed (Awaiting Client Signature)`}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Contract Content */}
            <div className="max-w-4xl mx-auto px-6 py-12 print:px-0 print:py-0 print:m-0 print:max-w-full">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="contract-card bg-white rounded-[2rem] md:rounded-[3rem] shadow-2xl shadow-indigo-900/5 ring-1 ring-gray-100 overflow-hidden print:rounded-none print:shadow-none print:ring-0"
                >
                    {/* Branding Header — Moved here to prevent extra page on print */}
                    <div className="bg-gray-50/50 border-b border-gray-100 px-6 py-3 flex items-center justify-center space-x-3 print:bg-white">
                        <p className="text-[8px] font-black uppercase tracking-widest text-gray-400">Authenticated By</p>
                        <BrandLogo iconSize="w-5 h-5" textSize="text-xs" />
                        <div className="h-3 w-px bg-gray-200" />
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Official CRM</span>
                    </div>

                    {/* Header Details */}
                    <div className="p-12 md:p-16 border-b border-gray-50">
                        <div className="flex justify-between items-start mb-8">
                            <div>
                                <h2 className="text-3xl font-black text-gray-900 mb-2 truncate max-w-lg">{contract.title}</h2>
                                <p className="text-sm text-gray-500 font-medium">Prepared for {project.client_name}</p>
                            </div>
                            <div className="text-right">
                                {user.company_name ? (
                                    <h3 className="text-xl font-black text-gray-900">{user.company_name}</h3>
                                ) : (
                                    <h3 className="text-xl font-black text-gray-900">{user.first_name} {user.last_name}</h3>
                                )}
                                <p className="text-xs text-gray-500 font-medium mt-1">{user.email}</p>
                                {user.address && (
                                    <p className="text-[10px] text-gray-400 font-medium mt-1 whitespace-pre-wrap">{user.address}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-6 text-[10px] font-black uppercase tracking-widest text-gray-400 border-t border-gray-50 pt-8 mt-4">
                            <div>
                                <span className="block mb-1">Status</span>
                                <span className={`px-3 py-1 rounded-full ${contract.status === 'signed' ? 'bg-teal-50 text-teal-600' : 'bg-amber-50 text-amber-600'}`}>
                                    {contract.status === 'signed' ? 'Fully Executed' : (contract.client_signature || contract.photographer_signature ? 'Partially Signed' : contract.status)}
                                </span>
                            </div>
                            <div>
                                <span className="block mb-1">Date Created</span>
                                <span className="text-gray-900">{new Date(contract.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Document Body */}
                    <div className="contract-body p-12 md:p-16 text-gray-800 leading-relaxed max-w-none text-sm md:text-base cursor-text selection:bg-indigo-100 break-words overflow-wrap-anywhere">
                        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: contract.body }} />
                    </div>

                    {/* Signature Section — always shows both parties */}
                    <div className="p-12 md:p-16 border-t border-gray-100 bg-gray-50/50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* CLIENT */}
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Client Signature</h4>
                                {contract.status === 'rejected' ? (
                                    <div className="bg-red-50 p-6 flex flex-col items-center rounded-2xl border border-red-100 shadow-sm text-center">
                                        <p className="font-serif text-2xl italic text-red-700 mb-2">Declined</p>
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-red-400">{project.client_name}</p>
                                        <div className="w-full h-px bg-red-100/50 my-4" />
                                        <p className="text-[10px] font-medium text-red-400">Declined on {new Date(contract.updated_at).toLocaleString()}</p>
                                    </div>
                                ) : contract.client_signature ? (
                                    <div className="bg-white p-6 flex flex-col items-center rounded-2xl border border-teal-100 shadow-sm">
                                        <p className="font-serif text-2xl italic text-gray-900 mb-2">{contract.client_signature}</p>
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{project.client_name}</p>
                                        <div className="w-full h-px bg-gray-100 my-4" />
                                        <p className="text-[10px] font-medium text-gray-400">Signed on {new Date(contract.signed_at).toLocaleString()}</p>
                                        <p className="text-[10px] font-medium text-gray-400">IP: {contract.signature_ip}</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSignContract} className="bg-white p-6 rounded-2xl border border-dashed border-gray-200 space-y-3 print-hidden">
                                        <p className="text-xs text-gray-500 font-medium">By signing, the client agrees to all terms above.</p>
                                        <input
                                            type="text"
                                            value={signatureName}
                                            onChange={(e) => setSignatureName(e.target.value)}
                                            placeholder={project.client_name}
                                            className="w-full bg-gray-50 border-none rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-teal-500 transition-all placeholder:text-gray-300"
                                            required
                                        />
                                        <button
                                            type="submit"
                                            disabled={isSigning || !signatureName.trim()}
                                            className="w-full py-3 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all"
                                        >
                                            {isSigning ? 'Signing...' : 'Sign as Client'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowRejectModal(true)}
                                            className="w-full py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-black uppercase tracking-widest text-xs transition-all mt-2"
                                        >
                                            Decline Contract
                                        </button>
                                    </form>
                                )}
                            </div>

                            {/* PHOTOGRAPHER */}
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Photographer Signature</h4>
                                {contract.photographer_signature ? (
                                    <div className="bg-white p-6 flex flex-col items-center rounded-2xl border border-teal-100 shadow-sm">
                                        <p className="font-serif text-2xl italic text-gray-900 mb-2">{contract.photographer_signature}</p>
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{user.company_name || `${user.first_name} ${user.last_name}`}</p>
                                        {contract.photographer_signed_at && (
                                            <>
                                                <div className="w-full h-px bg-gray-100 my-4" />
                                                <p className="text-[10px] font-medium text-gray-400">Signed on {new Date(contract.photographer_signed_at).toLocaleString()}</p>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 text-center print-hidden">
                                        <p className="text-xs text-gray-400 font-medium italic">Awaiting photographer countersign.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm print:hidden">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100"
                    >
                        <h3 className="text-xl font-black text-gray-900 mb-2">Decline Contract</h3>
                        <p className="text-sm text-gray-500 mb-6">Please let the photographer know why you are declining this contract.</p>
                        
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="w-full bg-gray-50 border-none rounded-2xl p-4 text-sm font-medium focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-gray-400 min-h-[120px] mb-6 resize-none"
                            placeholder="Reason for declining..."
                        />
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm tracking-wide hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRejectContract}
                                className="flex-1 py-3 px-4 bg-red-500 text-white rounded-xl font-bold text-sm tracking-wide hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
                            >
                                Decline
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
        </>
    );
};

export default ContractPage;
